import type { EyemedTabsBenefits } from '../types'
import { eyemedTabsBenefitsAreEmpty } from '../types'
import { normalizeMemberId } from '../../shared/memberId'

/**
 * The EyeMed panel's header slots — the pure half of what `EyemedSlideout`
 * hands `VisionSlideoutFrame` as `payerDetails`, so the sentences and the
 * "whose ID is this" rule are tested without the DOM.
 *
 * The header shows the member EyeMed RETURNED: the search is by name and
 * date of birth, so the benefits are the current member's whatever ID the
 * record holds, and printing the record's ID under them labelled the wrong
 * member (24 SC patients in one week, almost every earlier check reading
 * eligible under a replaced ID). The record's ID moves to a second line,
 * shown only when it differs — "Revolution has <ID>." on a check born from a
 * Revolution row, "On file: <ID>." on any other, a check with no recorded
 * source included (the weaker claim, always true) — spelled as the record
 * spells it. Nothing is computed from any of it beyond that comparison.
 */

/** Where a check's member ID came from, as the check doc records it
 *  (`EligibilityCheckDoc.memberIdSource`). */
export type MemberIdSource = 'revolution' | 'other'

/** A field the portal printed empty — and the Eligibility block's sentence
 *  for a table read with no rows. */
export const NOT_SHOWN = 'Not shown by EyeMed.'

/** The Group slot when the member block failed, and the Plan slot when the
 *  plan failed. */
export const COULD_NOT_READ = 'Could not be read. Try the check again.'

/** The Member ID slot's note when the member block failed. */
export const MEMBER_ID_COULD_NOT_READ = "EyeMed's member ID could not be read. Try the check again."

export interface EyemedHeaderValues {
  /** The Member ID slot's first line: EyeMed's ID as printed, the not-shown
   *  sentence, or — when the member block failed — the record's ID in its
   *  line form. Empty when there is nothing to print (the grid's dash). */
  memberId: string
  /** The line under it, or null: the record's ID ("Revolution has <ID>." /
   *  "On file: <ID>.") when it differs from EyeMed's or EyeMed printed none,
   *  or the could-not-be-read note when the block failed. */
  memberIdNote: string | null
  /** The Plan slot; null when the delivery carries no plan block (the dash). */
  plan: string | null
  /** The Group slot; null when the delivery carries no member block (the dash). */
  group: string | null
}

/** The record's ID in its line form, by the check's recorded source. */
export function recordIdLine(recordMemberId: string, memberIdSource: MemberIdSource | undefined): string {
  return memberIdSource === 'revolution'
    ? `Revolution has ${recordMemberId}.`
    : `On file: ${recordMemberId}.`
}

/** Two IDs count as the same when they still agree with separators stripped
 *  and case ignored — "1234-5678" is "12345678". */
export function sameMemberId(a: string | null | undefined, b: string | null | undefined): boolean {
  return normalizeMemberId(a).toUpperCase() === normalizeMemberId(b).toUpperCase()
}

/**
 * The header slots for a stored check, or null for today's header: a
 * delivery with no plan and no member block (every check stored before the
 * blocks, the retired bot's, a demo result), and a delivery the ingest read as
 * empty — the member-not-found doc, which still opens from a Cockpit row, the
 * Recovery Loop and a permalink, and whose header must state nothing about a
 * member the portal never found.
 */
export function eyemedHeader(
  benefits: EyemedTabsBenefits,
  recordMemberId: string | undefined,
  memberIdSource: MemberIdSource | undefined,
): EyemedHeaderValues | null {
  const { plan, member } = benefits
  if (!plan && !member) return null
  if (eyemedTabsBenefitsAreEmpty(benefits)) return null

  const record = (recordMemberId ?? '').trim()
  const line = record ? recordIdLine(record, memberIdSource) : null
  const planValue = !plan ? null : plan.state === 'failed' ? COULD_NOT_READ : plan.name ?? NOT_SHOWN

  if (!member) {
    return { memberId: record, memberIdNote: null, plan: planValue, group: null }
  }
  if (member.state === 'failed') {
    return { memberId: line ?? '', memberIdNote: MEMBER_ID_COULD_NOT_READ, plan: planValue, group: COULD_NOT_READ }
  }
  const group = member.group ?? NOT_SHOWN
  if (member.memberId === null) {
    // EyeMed printed no ID: the record's line stays under the slot, since the
    // one ID anyone can read is the record's.
    return { memberId: NOT_SHOWN, memberIdNote: line, plan: planValue, group }
  }
  return {
    memberId: member.memberId,
    memberIdNote: record && !sameMemberId(member.memberId, record) ? line : null,
    plan: planValue,
    group,
  }
}
